import React, { JSX, useEffect, useState } from "react";
import { useBaseSpaceConnection } from "@via/hooks/useBaseSpaceConnection.tsx";
import { BaseSpaceFile, DataSourceProvider } from "common";
import {
  useBaseSpaceAllFilesInProject,
  useBaseSpaceDatasetList,
  useBaseSpaceFileList,
  useBaseSpaceProjectList,
} from "@via/pages/data/data-sources/data-source.api.tsx";
import {
  BucketObject,
  BucketObjectTypes,
} from "@via/pages/data/data-sources/utils/helpers.tsx";
import { BookmarkIcon, HomeIcon } from "@heroicons/react/24/outline";
import { GenericError } from "@via/components/error.tsx";
import { ERROR } from "@via/utils/constants.tsx";
import { FileBrowser } from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/file-browser.tsx";
import SaveCurrentDataSource from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/additional-area/save-current-data-source.tsx";
import { AdditionalArea } from "@via/pages/data/data-sources/cloud-data-sources/amazon-s3/amazon-folder-browser.tsx";
import FileFilters from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/additional-area/file-filters.tsx";
import { Tooltip } from "react-tooltip";

const getDisplayablePathLinks = (
  project: BucketObject | null,
  dataset: BucketObject | null,
  handleHomeClick: () => void,
  handlePartialPathClick: (item: BucketObject) => void,
  isPending: boolean
): JSX.Element => {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        data-testid="folderBrowserHomeButton"
        className="flex h-8 items-center rounded-l-full rounded-r-full bg-indigo-600 px-6 py-2 text-white hover:bg-indigo-500"
        onClick={handleHomeClick}
        disabled={isPending}>
        <HomeIcon className="h-5 w-5" />
      </button>
      {project ? (
        <>
          <span>
            <button
              data-testid="folderBrowserPathButton"
              className="flex h-8 items-center rounded-l-full rounded-r-full bg-indigo-600 px-6 py-2 text-white hover:bg-indigo-500"
              disabled={isPending}
              onClick={() => {
                handlePartialPathClick(project);
              }}>
              {project.name} /
            </button>
          </span>
          {dataset ? (
            <span>
              <button
                data-testid="folderBrowserPathButton"
                className="flex h-8 items-center rounded-l-full rounded-r-full bg-indigo-600 px-6 py-2 text-white hover:bg-indigo-500"
                disabled={isPending}
                onClick={() => {
                  handlePartialPathClick(dataset);
                }}>
                {dataset.name} /
              </button>
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export const formatBasespaceObject = (
  objects: BaseSpaceFile[]
): BucketObject[] => {
  return objects.map(object => ({
    id: object.id,
    name: object.name,
    displayName: object.name,
    size: object.size ?? null,
    dateModified: object.dateModified ?? null,
    type:
      object.type === "File"
        ? BucketObjectTypes.File
        : BucketObjectTypes.Folder,
    baseSpaceFileType: object.type,
  }));
};

interface BaseSpaceFolderBrowserProps {
  onSelectedFilesUpdate: (items: BaseSpaceFile[]) => void;
}

const BaseSpaceFolderBrowser: React.FC<BaseSpaceFolderBrowserProps> = ({
  onSelectedFilesUpdate,
}) => {
  const [additionalArea, setAdditionalArea] = useState<AdditionalArea | null>(
    null
  );
  const [filter, setFilter] = useState("");
  const { connectionCredentials } = useBaseSpaceConnection();
  const [isBookmarkModalOpen, setIsBookmarkModalOpen] = useState(false);
  const [bucketObjectList, setBucketObjectList] = useState<BucketObject[]>([]);
  const [folderList, setFolderList] = useState<BaseSpaceFile[]>([]);
  const [currentProject, setCurrentProject] = useState<BucketObject | null>(
    null
  );
  const [currentDataset, setCurrentDataset] = useState<BucketObject | null>(
    null
  );
  const [currentPath, setCurrentPath] = useState<string>("");

  useEffect(() => {
    const path =
      currentProject && currentDataset
        ? `${currentProject.id}/${currentDataset.id}`
        : currentProject
          ? `${currentProject.id}`
          : "";
    setCurrentPath(path);
  }, [currentProject, currentDataset]);

  useEffect(() => {
    setBucketObjectList(formatBasespaceObject(folderList));
  }, [folderList]);

  const {
    mutateAsync: getBaseSpaceProjectList,
    isPending: isGetBaseSpaceProjectListLoading,
    isError: isGetBaseSpaceProjectListError,
  } = useBaseSpaceProjectList();

  const {
    mutateAsync: getBaseSpaceDatasetList,
    isPending: isGetBaseSpaceDatasetListLoading,
    isError: isGetBaseSpaceDatasetListError,
  } = useBaseSpaceDatasetList();

  const {
    mutateAsync: getBaseSpaceFileList,
    isPending: isGetBaseSpaceFileListLoading,
    isError: isGetBaseSpaceFileListError,
  } = useBaseSpaceFileList();

  const {
    mutateAsync: getBaseSpaceExtraFileList,
    isPending: isGetBaseSpaceExtraFileListLoading,
  } = useBaseSpaceFileList();

  const {
    mutateAsync: getBaseSpaceAllFilesInProject,
    isPending: isGetBaseSpaceAllFilesInProjectLoading,
  } = useBaseSpaceAllFilesInProject();

  const isLoading =
    isGetBaseSpaceProjectListLoading ||
    isGetBaseSpaceDatasetListLoading ||
    isGetBaseSpaceFileListLoading;
  const isError =
    isGetBaseSpaceProjectListError ||
    isGetBaseSpaceDatasetListError ||
    isGetBaseSpaceFileListError;

  const isExtraFilesLoading =
    isGetBaseSpaceExtraFileListLoading ||
    isGetBaseSpaceAllFilesInProjectLoading;

  useEffect(() => {
    if (connectionCredentials) {
      getBaseSpaceProjectList(connectionCredentials).then(response => {
        setFolderList(response);
        setCurrentProject(null);
        setCurrentDataset(null);
      });
    }
  }, [connectionCredentials]);

  const handleObjectClick = (object: BucketObject) => {
    if (object.id === "..") {
      if (currentProject && currentDataset) {
        getBaseSpaceDatasetList({
          tokenId: connectionCredentials?.tokenId as number,
          projectId: currentProject.id!,
        }).then(response => {
          setFolderList(response);
          setCurrentDataset(null);
        });
      } else if (currentProject) {
        handleHomeClick();
      }
      return;
    }

    if (object.baseSpaceFileType === "Project") {
      getBaseSpaceDatasetList({
        tokenId: connectionCredentials?.tokenId as number,
        projectId: object.id!,
      }).then(response => {
        setFolderList(response);
        setCurrentProject(object);
        setCurrentDataset(null);
      });
    } else if (object.baseSpaceFileType === "Dataset") {
      getBaseSpaceFileList({
        tokenId: connectionCredentials?.tokenId as number,
        datasetId: object.id!,
      }).then(response => {
        setFolderList(response);
        setCurrentDataset(object);
      });
    } else {
      return;
    }
  };

  const handleExtraFolderObjects = async (object: BucketObject) => {
    if (object.baseSpaceFileType === "Project") {
      const allFilesResponse = await getBaseSpaceAllFilesInProject({
        tokenId: connectionCredentials?.tokenId as number,
        projectId: object.id!,
      });
      return allFilesResponse || [];
    } else if (object.baseSpaceFileType === "Dataset") {
      const datasetItemsResponse = await getBaseSpaceExtraFileList({
        tokenId: connectionCredentials?.tokenId as number,
        datasetId: object.id!,
      });
      return datasetItemsResponse || [];
    }
  };

  const handleHomeClick = () => {
    if (connectionCredentials) {
      getBaseSpaceProjectList(connectionCredentials).then(response => {
        setFolderList(response);
        setCurrentProject(null);
        setCurrentDataset(null);
      });
    }
  };

  const handlePartialPathClick = (item: BucketObject) => {
    handleObjectClick(item);
  };

  const handleAdditionalAreaButtonClick = (value: AdditionalArea | null) => {
    if (value === additionalArea) {
      setAdditionalArea(null);
    } else {
      setAdditionalArea(value);
    }
  };

  return (
    <div>
      {isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <div>
          <div className="mt-2 gap-2 flex justify-between flex-wrap">
            {getDisplayablePathLinks(
              currentProject,
              currentDataset,
              handleHomeClick,
              handlePartialPathClick,
              false
            )}

            <button
              id="basespace-file-browser-bookmark-button"
              className="inline-flex justify-center rounded-md bg-indigo-600 p-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={() => setIsBookmarkModalOpen(true)}>
              <BookmarkIcon className="h-5 w-5" />
            </button>
            <Tooltip
              anchorSelect="#basespace-file-browser-bookmark-button"
              content="Bookmark this location details as a datasource for quick access."
            />
          </div>

          {isBookmarkModalOpen && (
            <SaveCurrentDataSource
              dataSourceProvider={DataSourceProvider.BaseSpace}
              credentialId={connectionCredentials?.tokenId as number}
              path={currentPath}
              open={isBookmarkModalOpen}
              setOpen={setIsBookmarkModalOpen}
            />
          )}

          {additionalArea ? (
            <div className="mt-4 rounded-lg border bg-blue-50/50 px-3 py-4">
              {additionalArea === AdditionalArea.Filters ? (
                <FileFilters filterValue={filter} setFilterValue={setFilter} />
              ) : null}
            </div>
          ) : null}

          <FileBrowser
            dataSourceProvider={DataSourceProvider.BaseSpace}
            objects={bucketObjectList}
            onObjectClick={handleObjectClick}
            getExtraFolderObjects={handleExtraFolderObjects}
            onContinue={onSelectedFilesUpdate}
            isGetFolderListLoading={isLoading}
            isGetExtraFolderListLoading={isExtraFilesLoading}
            filterValue={filter}
            onAdditionalAreaButtonClick={handleAdditionalAreaButtonClick}
            additionalArea={additionalArea}
            setAdditionalArea={setAdditionalArea}
          />
        </div>
      )}
    </div>
  );
};

export default BaseSpaceFolderBrowser;
